import React, { useState } from "react";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  addKnowledgeEntry,
  isSeedEntry,
  KnowledgeScope,
  removeKnowledgeEntry,
  setScopeSummary,
} from "./knowledgeBankStore";
import { KnowledgeEntry } from "./types";

interface KnowledgeTierSectionProps {
  scope: KnowledgeScope;
  /** Stable slug for test ids and input ids, e.g. "org" or a practice id. */
  slug: string;
  /** Who these answers are used for, e.g. "All locations" or "Oak Hill only". */
  title: string;
  /** Same thing worded to finish "Save this answer for …". */
  saveTargetLabel: string;
  summary: string;
  /** Entries after any active search; the count reflects the whole group. */
  entries: KnowledgeEntry[];
  totalCount: number;
  isSearching: boolean;
  highlightedId: string | null;
}

/**
 * One group of answers — the set used at every location, or the set used at a
 * single clinic — with the controls to add a question and to rewrite the
 * description of what the group covers.
 */
function KnowledgeTierSection({
  scope,
  slug,
  title,
  saveTargetLabel,
  summary,
  entries,
  totalCount,
  isSearching,
  highlightedId,
}: KnowledgeTierSectionProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [draftSummary, setDraftSummary] = useState(summary);

  const canSaveEntry = question.trim() !== "" && answer.trim() !== "";

  const closeAddForm = () => {
    setIsAdding(false);
    setQuestion("");
    setAnswer("");
  };

  const saveEntry = () => {
    if (!canSaveEntry) {
      return;
    }
    addKnowledgeEntry({ scope, question, answer });
    closeAddForm();
  };

  const startEditingContext = () => {
    setDraftSummary(summary);
    setIsEditingContext(true);
  };

  const saveContext = () => {
    const trimmed = draftSummary.trim();
    if (trimmed !== "") {
      setScopeSummary(scope, trimmed);
    }
    setIsEditingContext(false);
  };

  return (
    <Card className="gap-3 py-4" data-testid={`kb-section-${slug}`}>
      <CardHeader className="px-4">
        <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={scope.tier === "ORG" ? "secondary" : "outline"}
              className="text-[10px]"
            >
              {title}
            </Badge>
            <span className="text-xs font-normal text-gray-500">
              {totalCount} {totalCount === 1 ? "answer" : "answers"}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              data-testid={`kb-edit-context-${slug}`}
              onClick={
                isEditingContext
                  ? () => setIsEditingContext(false)
                  : startEditingContext
              }
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit description
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              data-testid={`kb-add-entry-${slug}`}
              onClick={isAdding ? closeAddForm : () => setIsAdding(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add question
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 space-y-2">
        {isEditingContext ? (
          <div className="space-y-2 rounded-md border border-brand-100 bg-brand-50 p-3">
            <Label
              htmlFor={`kb-context-input-${slug}`}
              className="text-xs text-gray-700"
            >
              What this group of answers covers
            </Label>
            <Textarea
              id={`kb-context-input-${slug}`}
              value={draftSummary}
              onChange={(event) => setDraftSummary(event.target.value)}
              data-testid={`kb-context-input-${slug}`}
              className="text-xs min-h-24 bg-white"
            />
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                className="h-7 text-xs"
                data-testid={`kb-save-context-${slug}`}
                disabled={draftSummary.trim() === ""}
                onClick={saveContext}
              >
                <Check className="h-3 w-3 mr-1" />
                Save description
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setIsEditingContext(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-600" data-testid={`kb-context-${slug}`}>
            {summary}
          </p>
        )}

        {isAdding && (
          <div
            className="space-y-2 rounded-md border border-brand-100 bg-brand-50 p-3"
            data-testid={`kb-add-form-${slug}`}
          >
            <div className="space-y-1">
              <Label
                htmlFor={`kb-question-input-${slug}`}
                className="text-xs text-gray-700"
              >
                Question callers ask
              </Label>
              <Input
                id={`kb-question-input-${slug}`}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                data-testid={`kb-question-input-${slug}`}
                placeholder="e.g. Do you offer sibling discounts?"
                className="h-8 text-xs bg-white"
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor={`kb-answer-input-${slug}`}
                className="text-xs text-gray-700"
              >
                Answer the assistant should give
              </Label>
              <Textarea
                id={`kb-answer-input-${slug}`}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                data-testid={`kb-answer-input-${slug}`}
                placeholder="Write the answer exactly as you would want it said on a call."
                className="text-xs min-h-20 bg-white"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                className="h-7 text-xs"
                data-testid={`kb-save-entry-${slug}`}
                disabled={!canSaveEntry}
                onClick={saveEntry}
              >
                <Check className="h-3 w-3 mr-1" />
                Save this answer for {saveTargetLabel}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={closeAddForm}
              >
                Cancel
              </Button>
            </div>
            <p className="text-[10px] text-gray-600">
              The assistant can use this answer on future calls. Calls already
              handled stay exactly as they are.
            </p>
          </div>
        )}

        {entries.length === 0 ? (
          <p className="text-xs text-gray-500 italic pt-1">
            {isSearching
              ? "No answers in this group match your search."
              : "No answers yet — add the first question this group should cover."}
          </p>
        ) : (
          entries.map((entry) => {
            const isAdded = !isSeedEntry(entry.id);
            return (
              <div
                key={entry.id}
                id={entry.id}
                data-testid={`kb-entry-${entry.id}`}
                aria-current={entry.id === highlightedId ? "true" : undefined}
                className={`rounded-md border px-3 py-2 ${
                  entry.id === highlightedId
                    ? "border-brand bg-brand-50 ring-2 ring-brand-100"
                    : "border-border bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    “{entry.question}”
                  </p>
                  {isAdded && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-xs text-gray-500 hover:text-red-700 shrink-0"
                      aria-label={`Remove entry: ${entry.question}`}
                      data-testid={`kb-remove-${entry.id}`}
                      onClick={() => removeKnowledgeEntry(entry.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-1">{entry.answer}</p>
                <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1.5 flex-wrap">
                  <span>Last updated {entry.lastReviewedAt}</span>
                  {isAdded && (
                    <Badge
                      variant="outline"
                      className="text-[9px] border-brand-100 text-brand"
                    >
                      Added in this session
                    </Badge>
                  )}
                </p>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default KnowledgeTierSection;
